import React, { useCallback, useMemo, useState } from "react";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@apollo/client";
import { useRouteMatch, Link } from "react-router-dom";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import {
  QUERY_MINEW,
  QUERY_MINEW_DEVICES,
  QUERY_MINEW_ITEMS,
  QUERY_MINEWS,
} from "../../../config/graphql/query";
import { DELETE_MINEW, RESTART_MINEW } from "../../../config/graphql/mutation";

import Table from "../../../components/Table";

import RoleBlock from "../../../components/RoleBlock";
import useHasRole from "../../../lib/hooks/useHasRole";
import { useCurrentClient } from "../../../context/Client";
import { useCenterParcs } from "../../../lib/hooks/useCenterParcs";

const TableRow = ({ item, index }: { item: IMinew; index: number }) => {
  const { id, title, template, items, device } = item as IMinew & {
    device?: { mac?: string };
  };

  const { t } = useTranslation(["minew", "common"]);

  const isAdmin = useHasRole(["ADMIN"]);

  const { isCenterParcsClient } = useCenterParcs();

  const [modal, setModal] = useState<"remove" | "reload" | null>(null);

  const deviceData = items && items.length > 0 ? items[0].minew?.device : null;
  const mac = device?.mac || deviceData?.mac;

  const { data: minewData } = useQuery(QUERY_MINEW, {
    variables: { mac },
    skip: !mac,
  });

  const { data } = useQuery(QUERY_MINEW_ITEMS, {
    variables: {
      filter: {
        minew: id,
      },
    },
  });

  const minewItems = data?.minewItems ?? [];

  const [onDelete, { loading: deleteLoading }] = useMutation(DELETE_MINEW, {
    refetchQueries: [
      { query: QUERY_MINEWS, variables: { sort: { title: "ASC" } } },
      { query: QUERY_MINEW_DEVICES },
    ],
    variables: { id },
  });

  const onRemove = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();

      try {
        await onDelete();

        toast.success<string>(t("minew:minews:toast.deleted"));
      } catch (error) {
        toast.error<string>(t("minew:minews:toast.deletedError"));
      }
      setModal(null);
    },
    [onDelete, t],
  );

  const onBeforeRemove = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();

      setModal("remove");
    },
    [],
  );

  const [onRestart, { loading: restartLoading }] = useMutation(RESTART_MINEW, {
    variables: { id },
  });
  const onReload = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();

      try {
        await onRestart();

        toast.success<string>(t("minew:minews:toast.reloaded"));
      } catch (error) {
        toast.error<string>(t("minew:minews:toast.reloadedError"));
      }
      setModal(null);
    },
    [onRestart, t],
  );

  const onBeforeReload = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();

      setModal("reload");
    },
    [],
  );
  const isOnlineRaw = minewData?.minewDeviceByMac?.data?.isOnline;
  // Minew API returns: "2" = online, "1" = offline, anything else = idle/unknown.
  let status: "Online" | "Offline" | "Idle";

  const normalized =
    isOnlineRaw === undefined || isOnlineRaw === null
      ? ""
      : String(isOnlineRaw);

  if (normalized === "2") {
    status = "Online";
  } else if (normalized === "1") {
    status = "Offline";
  } else {
    status = "Idle";
  }

  const isViewDisabled = !isAdmin && !minewItems.length;

  return (
    <>
      <tr>
        <th scope="row" className="text-nowrap">
          {index + 1}
        </th>
        <td>{title}</td>
        <td>{template}</td>
        <td
          className={`${
            // eslint-disable-next-line no-nested-ternary
            status === "Online"
              ? "bg-primary"
              : status === "Offline"
              ? "bg-danger"
              : "bg-warning"
          } text-white`}
        >
          {status}
        </td>
        <td className="text-right">
          {minewData?.minewDeviceByMac?.data?.battery} %
        </td>
        <td className="text-nowrap">
          <RoleBlock roles={["ADMIN"]}>
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={onBeforeRemove}
            >
              {t("common:delete")}
            </Button>
          </RoleBlock>
          <Button
            as={Link}
            to={
              isViewDisabled
                ? "#"
                : /**
                   * The fields tab only exists for center-parcs; sending any
                   * other client's users there lands them on a blank page.
                   * Everyone else gets the information tab, which carries the
                   * label preview.
                   */
                  `/minew-devices/${id}${
                    !isAdmin && isCenterParcsClient ? "/template-fields" : ""
                  }`
            }
            size="sm"
            className="mr-2"
            disabled={isViewDisabled}
            onClick={(e) => {
              if (isViewDisabled) e.preventDefault();
            }}
          >
            {t("common:view")}
          </Button>
          <Button size="sm" variant="secondary" onClick={onBeforeReload}>
            {t("common:reload")}
          </Button>
        </td>
      </tr>
      <Modal
        show={!!modal}
        onHide={() => setModal(null)}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t(`minew:minews.modal.${modal}.title`)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(`minew:minews.modal.${modal}.body`, {
            title: item.title,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setModal(null)}>
            {t("common:cancel")}
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={restartLoading || deleteLoading}
            onClick={(e) => {
              if (modal === "reload") {
                onReload(e);
              }
              if (modal === "remove") {
                onRemove(e);
              }
            }}
          >
            {modal === "remove" && t("common:delete")}
            {modal === "reload" && t("common:reload")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const MinewDevices = React.memo(() => {
  const match = useRouteMatch();

  const { t } = useTranslation(["minew", "common"]);

  const currentClient = useCurrentClient();

  const { data } = useQuery(QUERY_MINEWS, {
    variables: { sort: { title: "ASC" } },
  });

  /**
   * The sort variable above never reaches the server — QUERY_MINEWS declares
   * no variables and the minews resolver ignores the options the mongo query
   * directive prepares — so screens arrive in Mongo's natural order. Ename's
   * nameplates are numbered and have to read Device1..DeviceN, so they are
   * sorted here; every other client keeps the order it has today. Numeric
   * compare, or Device10 would land before Device2.
   */
  const minewDevices: Array<IMinew> = useMemo(() => {
    const screens: Array<IMinew> = data?.minews ?? [];

    if (currentClient?.code !== "ename") {
      return screens;
    }

    return [...screens].sort((a, b) =>
      (a.title ?? "").localeCompare(b.title ?? "", undefined, {
        numeric: true,
      }),
    );
  }, [data, currentClient]);

  const renderMinew = useCallback(
    (item: IMinew, index: number) => (
      <TableRow key={item.id} item={item} index={index} />
    ),
    [],
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            {t("minew:minews.nav.screens")}
          </li>
        </ol>
      </nav>
      <RoleBlock roles={["ADMIN"]}>
        <div className="d-flex justify-content-end align-items-center mb-3">
          <Link
            to={`${match.path}/new`}
            type="button"
            className="btn btn-primary"
          >
            {t("minew:minews.button.add")}
          </Link>
        </div>
      </RoleBlock>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">{t("minew:minews.th.name")}</th>
            <th scope="col">{t("minew:minews.th.template")}</th>
            <th scope="col">{t("minew:minews.th.status")}</th>
            <th scope="col" className="text-right">
              {t("minew:minews.th.battery")}
            </th>
            <th scope="col">{t("minew:minews.th.actions")}</th>
          </tr>
        </thead>
        <tbody>{minewDevices.map(renderMinew)}</tbody>
      </Table>
    </div>
  );
});

export default MinewDevices;
